"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
const useWvcLanguage = () => {
  const [lang, setLang] = React.useState<"en" | "zh" | "fr" | "es" | "de">("en");
  React.useEffect(() => {
    const getLang = () => {
      const htmlLang = document.documentElement.lang?.slice(0, 2).toLowerCase();
      const winLang = (window as any)?.WVC_LANG?.slice(0, 2)?.toLowerCase();
      const active = winLang || htmlLang || "en";
      if (["en", "zh", "fr", "es", "de"].includes(active)) {
        setLang(active as "en" | "zh" | "fr" | "es" | "de");
      } else {
        setLang("en");
      }
    };
    getLang();
    window.addEventListener("languagechange", getLang);
    window.addEventListener("wvc-language-change", getLang);
    return () => {
      window.removeEventListener("languagechange", getLang);
      window.removeEventListener("wvc-language-change", getLang);
    };
  }, []);
  return lang;
};
const translations = {
  en: {
    season: "Autumn / Winter Capsule",
    edit: "The New Season Edit",
    description: "Effortless silhouettes designed for every version of you.",
    shopDresses: "Shop Dresses",
    exploreArrivals: "Explore New Arrivals",
    tailored: "Tailored",
    craftsmanship: "Craftsmanship",
    premium: "Premium",
    naturalFibers: "Natural Fibers",
    worldwide: "Worldwide",
    expressDelivery: "Express Delivery"
  },
  zh: {
    season: "秋冬季胶囊",
    edit: "新季节编辑",
    description: "为你的每一个版本设计的轻松剪裁。",
    shopDresses: "购买连衣裙",
    exploreArrivals: "探索新品上市",
    tailored: "裁剪",
    craftsmanship: "工艺",
    premium: "高级",
    naturalFibers: "天然纤维",
    worldwide: "全球",
    expressDelivery: "快速递送"
  },
  fr: {
    season: "Capsule Automne / Hiver",
    edit: "L'Édition de la Nouvelle Saison",
    description: "Des silhouettes sans effort conçues pour chaque version de vous.",
    shopDresses: "Acheter des Robes",
    exploreArrivals: "Explorez les Nouveautés",
    tailored: "Taillé",
    craftsmanship: "Artisanat",
    premium: "Premium",
    naturalFibers: "Fibres Naturelles",
    worldwide: "Mondial",
    expressDelivery: "Livraison Express"
  },
  es: {
    season: "Cápsula Otoño / Invierno",
    edit: "La Edición de la Nueva Temporada",
    description: "Siluetas sin esfuerzo diseñadas para cada versión de ti.",
    shopDresses: "Comprar Vestidos",
    exploreArrivals: "Explorar Nuevas Llegadas",
    tailored: "Ajustado",
    craftsmanship: "Artesanía",
    premium: "Premium",
    naturalFibers: "Fibras Naturales",
    worldwide: "Mondial",
    expressDelivery: "Entrega Rápida"
  },
  de: {
    season: "Herbst- / Winterkapsel",
    edit: "Die Neuauflage der neuen Saison",
    description: "Mühelose Silhouetten für jede Version von dir.",
    shopDresses: "Kleider Kaufen",
    exploreArrivals: "Neue Ankünfte Erkunden",
    tailored: "Maßgeschneidert",
    craftsmanship: "Handwerk",
    premium: "Premium",
    naturalFibers: "Naturfasern",
    worldwide: "Weltweit",
    expressDelivery: "Express-Lieferung"
  }
};
export default function Hero() {
  const language = useWvcLanguage();
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.1,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="hero大图区" data-nav="light" className="relative w-full min-h-[85vh] lg:min-h-[92vh] flex items-center bg-background overflow-hidden border-b border-border">
      {/* Background Image with subtle scale animation */}
      <div className="absolute inset-0 z-0 overflow-hidden">
        <motion.div initial={{
        scale: shouldReduceMotion ? 1 : 1.04
      }} animate={{
        scale: 1
      }} transition={{
        duration: 0.9,
        ease: [0.16, 1, 0.3, 1]
      }} className="relative w-full h-full">
          <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1794104/img/editorial_fashion_hero_model.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1794104/img/editorial_fashion_hero_model.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1794104/img/editorial_fashion_hero_model.webp-wvc-sizes" alt="High fashion model in tailored ivory structured dress in natural daylight" loading="eager" decoding="async" className="w-full h-full object-cover object-center lg:object-[65%_center]" />
          {/* Subtle Warm Ivory Editorial Gradient Overlay for Text Legibility */}
          <div className="absolute inset-0 bg-linear-to-r from-background/95 via-background/70 to-background/20 lg:from-background/90 lg:via-background/40 lg:to-transparent" />
          <div className="absolute inset-0 bg-linear-to-t from-background/60 via-transparent to-background/20" />
        </motion.div>
      </div>

      {/* Main Editorial Container */}
      <div className="container relative z-10 mx-auto px-6 sm:px-10 lg:px-16 py-16 lg:py-24 max-w-7xl">
        <motion.div variants={containerVariants} initial="hidden" animate="visible" className="max-w-2xl text-left">
          {/* Eyebrow / Collection Tag */}
          <motion.div variants={itemVariants} className="inline-flex items-center gap-2 mb-6">
            <span className="w-8 h-px bg-foreground/40" />
            <span className="text-xs uppercase tracking-widest font-semibold text-muted-foreground font-sans">
              {translations[language].season}
            </span>
          </motion.div>

          {/* Editorial Headline */}
          <motion.h1 variants={itemVariants} className="font-serif text-4xl sm:text-6xl lg:text-7xl font-normal leading-[1.08] tracking-tight text-foreground text-balance">
            {translations[language].edit}
          </motion.h1>

          {/* Subheadline */}
          <motion.p variants={itemVariants} className="mt-6 text-base sm:text-lg lg:text-xl font-light text-foreground/80 leading-relaxed max-w-xl font-sans">
            {translations[language].description}
          </motion.p>

          {/* CTA Action Buttons */}
          <motion.div variants={itemVariants} className="mt-10 flex flex-col sm:flex-row items-stretch sm:items-center gap-4 sm:gap-5">
            <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-none px-8 py-6 text-sm font-medium tracking-wide shadow-none transition-[color,background-color] duration-200">
              <Link to="/ShopPage">
                {translations[language].shopDresses}
                <ArrowUpRight className="size-4 ml-1" />
              </Link>
            </Button>

            <Button asChild variant="secondary" size="lg" className="bg-secondary text-secondary-foreground hover:bg-secondary/85 rounded-none px-8 py-6 text-sm font-medium tracking-wide shadow-none transition-[color,background-color] duration-200 border border-secondary">
              <Link to="/ShopPage">
                {translations[language].exploreArrivals}
              </Link>
            </Button>
          </motion.div>

          {/* Micro Brand Highlights */}
          <motion.div variants={itemVariants} className="mt-14 pt-8 border-t border-border/80 flex flex-wrap gap-8 items-center text-xs text-muted-foreground tracking-wider uppercase font-sans">
            <div>
              <span className="font-semibold text-foreground">
                {translations[language].tailored}
              </span>{" "}
              {translations[language].craftsmanship}
            </div>
            <div className="w-1 h-1 rounded-full bg-border" />
            <div>
              <span className="font-semibold text-foreground">
                {translations[language].premium}
              </span>{" "}
              {translations[language].naturalFibers}
            </div>
            <div className="w-1 h-1 rounded-full bg-border" />
            <div>
              <span className="font-semibold text-foreground">
                {translations[language].worldwide}
              </span>{" "}
              {translations[language].expressDelivery}
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}